import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { BookOpen, UserCircle, GraduationCap, Trash2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Register() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("student");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleClearCache = async () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      document.cookie.split(";").forEach(c => {
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(reg => reg.unregister()));
      }
      toast.success('Cache cleared! The page will reload...');
      setTimeout(() => {
        window.location.href = window.location.origin + '/register?cache_bust=' + Date.now();
      }, 1000);
    } catch (error) {
      toast.error('Failed to clear cache. Please try manually clearing browser data.');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ email, password, name, role })
      });
      
      const data = await response.json();
      
      if (response.ok && data.token) {
        localStorage.setItem('auth_token', data.token);
        toast.success('Account created successfully!');
        navigate('/dashboard');
      } else {
        toast.error(data.detail || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Unable to connect. Please check your internet connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex">
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{ backgroundImage: `url('https://images.unsplash.com/photo-1639753759816-fd5442a41d1f?q=85&w=800&auto=format&fit=crop')` }}
      >
        <div className="absolute inset-0 bg-[#3A5A40]/40"></div>
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <h1 className="text-5xl font-bold mb-4" style={{ fontFamily: 'Merriweather, serif' }}>Join Our Community</h1>
          <p className="text-xl leading-relaxed">Start your spiritual journey with expert-led Bible courses.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-[#FAEDCD]">
        <Card className="w-full max-w-md p-8 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-lg rounded-2xl">
          <div className="flex items-center justify-center mb-6">
            <BookOpen className="w-12 h-12 text-[#3A5A40]" strokeWidth={1.5} />
          </div>
          <h2 className="text-3xl font-bold text-center mb-2 text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>Create Account</h2>
          <p className="text-center text-[#588157] mb-6">Begin your faith journey today</p>

          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-[#344E41]">Full Name</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="register-name-input"
                className="bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg placeholder:text-[#A3B18A]"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-[#344E41]">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="register-email-input"
                className="bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg placeholder:text-[#A3B18A]"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[#344E41]">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                data-testid="register-password-input"
                className="bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg placeholder:text-[#A3B18A]"
              />
            </div>
            <div>
              <Label className="text-[#344E41] mb-3 block">I am a...</Label>
              <RadioGroup value={role} onValueChange={setRole} className="flex gap-4">
                <div className="flex items-center space-x-2 flex-1">
                  <RadioGroupItem value="student" id="student" data-testid="role-student-radio" className="border-[#3A5A40] text-[#3A5A40]" />
                  <Label htmlFor="student" className="flex items-center gap-2 cursor-pointer text-[#344E41]">
                    <UserCircle className="w-5 h-5" strokeWidth={1.5} />
                    Student
                  </Label>
                </div>
                <div className="flex items-center space-x-2 flex-1">
                  <RadioGroupItem value="teacher" id="teacher" data-testid="role-teacher-radio" className="border-[#3A5A40] text-[#3A5A40]" />
                  <Label htmlFor="teacher" className="flex items-center gap-2 cursor-pointer text-[#344E41]">
                    <GraduationCap className="w-5 h-5" strokeWidth={1.5} />
                    Teacher
                  </Label>
                </div>
              </RadioGroup>
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="register-submit-button"
              className="w-full bg-[#3A5A40] text-white hover:bg-[#344E41] shadow-md duration-300 hover:shadow-lg active:scale-95 font-medium rounded-lg"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-[#CCD5AE]"></span>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-[#FEFAE0] px-4 text-[#A3B18A]">Or continue with</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleSignup}
            data-testid="google-signup-button"
            className="w-full border-2 border-[#3A5A40] text-[#3A5A40] hover:bg-[#3A5A40] hover:text-white duration-300 rounded-lg"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign up with Google
          </Button>

          <p className="text-center mt-6 text-[#588157]">
            Already have an account?{' '}
            <Link to="/login" className="text-[#3A5A40] font-medium hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>

          {/* Clear Cache Button for troubleshooting */}
          <div className="mt-6 pt-4 border-t border-[#CCD5AE]/50">
            <Button
              type="button"
              variant="ghost"
              onClick={handleClearCache}
              data-testid="clear-cache-button"
              className="w-full text-[#A3B18A] hover:text-[#588157] hover:bg-[#E9EDC9]/50 text-sm"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Having issues? Clear browser cache
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}